import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

function BlogDetail() {
  const { slug } = useParams();
  const [berita, setBerita] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`https://api.rf-funserver.my.id/api/berita/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('Berita tidak ditemukan');
        return res.json();
      })
      .then((data) => {
        setBerita(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Memuat berita...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-2xl font-bold mb-4">Berita Tidak Ditemukan</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-full font-semibold transition"
          >
            ← Kembali ke Home
          </Link>
        </div>
      </div>
    );
  }

  if (!berita) return null;

  return (
    <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white min-h-screen">
      {/* Header with Breadcrumb */}
      <div className="bg-gray-900/50 border-b border-gray-700 py-4">
        <div className="max-w-4xl mx-auto px-4">
          <nav className="flex items-center text-sm text-gray-400 space-x-2">
            <Link to="/" className="hover:text-orange-400 transition">Home</Link>
            <span>›</span>
            <Link to="/" className="hover:text-orange-400 transition">Berita</Link>
            <span>›</span>
            <span className="text-gray-500 truncate">{berita.judul}</span>
          </nav>
        </div>
      </div>

      {/* Article Container */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Article Header */}
        <header className="mb-8">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            {berita.judul}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <time dateTime={berita.created_at}>
                {new Date(berita.created_at).toLocaleDateString('id-ID', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </time>
            </div>
            <span>•</span>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              <span>RF FUN Team</span>
            </div>
          </div>

          {/* Excerpt */}
          <p className="text-xl text-gray-300 leading-relaxed bg-gray-800/50 p-6 rounded-lg border-l-4 border-orange-500">
            {berita.excerpt}
          </p>
        </header>

        {/* Featured Image */}
        {berita.thumbnail && (
          <div className="mb-10">
            <img
              src={berita.thumbnail}
              alt={berita.judul}
              className="w-full h-auto rounded-xl shadow-2xl"
            />
          </div>
        )}

        {/* Article Content */}
        <div
          className="prose prose-invert prose-lg max-w-none
            prose-headings:text-orange-400 prose-headings:font-bold
            prose-p:text-gray-300 prose-p:leading-relaxed
            prose-strong:text-orange-400 prose-strong:font-semibold
            prose-em:text-gray-300
            prose-ul:text-gray-300 prose-ol:text-gray-300
            prose-li:text-gray-300 prose-li:marker:text-orange-500
            prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-orange-500 prose-blockquote:text-gray-300
            prose-code:text-orange-300 prose-code:bg-gray-800
            prose-pre:bg-gray-800 prose-pre:text-gray-300
            prose-img:rounded-lg prose-img:shadow-xl
            prose-hr:border-gray-700
            prose-table:text-gray-300"
          dangerouslySetInnerHTML={{ __html: berita.isi }}
        />

        {/* Share & Back */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-6 py-3 rounded-full font-semibold transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Kembali ke Home
            </Link>

            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Share:</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-blue-600 hover:bg-blue-700 rounded-full transition"
                title="Share di Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${berita.judul}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-sky-500 hover:bg-sky-600 rounded-full transition"
                title="Share di Twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related News or CTA */}
      <section className="bg-gradient-to-b from-gray-800 to-gray-900 py-16 mt-12 border-t border-gray-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold mb-4">Tertarik Bermain?</h3>
          <p className="text-gray-300 mb-8">
            Download client sekarang dan bergabung dengan ribuan player di RF FUN SERVER!
          </p>
          <Link
            to="/download"
            className="inline-block bg-orange-600 hover:bg-orange-700 px-8 py-4 rounded-full font-bold text-white shadow-lg transition transform hover:scale-105"
          >
            🚀 Download Sekarang
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogDetail;
